import { CUSTOM_ERROR_CODES } from '../constants';
import { customInteraction, noticeError } from './newrelic';
import refetchSources from './refetchSources';

interface PvxItem {
  mediaId: string;
  title: string;
  type: 'LIVE' | 'VOD';
  status: 'active' | 'inactive' | 'error';
  thumbnailUrl: string | null;
  description: string;
  duration: number | null;
  seriesSlug: string | null;
  seriesTitle: string | null;
  offerId: string | null;
  price: number | null;
  currency: string | null;
  offerStart: string | null;
  offerEnd: string | null;
  publishStartDate: string | null;
  publishEndDate: string | null;
  createdAt: string;
  updatedAt: string;
  metadata?: Record<string, string | number>;
  hasAccess?: boolean;
  sources?: Source[];
  tracks?: {
    label?: string;
    kind: 'thumbnails' | 'captions';
    file: string;
  }[];
  offerRichTextId?: string | null;
}

function getErrorDetails(errorCode: number): { [key: string]: number | string | null } {
  return {
    error_code: errorCode,
    error_group:
      Object.keys(CUSTOM_ERROR_CODES).find(
        (key) => errorCode === CUSTOM_ERROR_CODES[key as keyof typeof CUSTOM_ERROR_CODES],
      ) || null,
  };
}

export default async function getVideo({
  guid,
  videoApiURL,
  contentUrl,
  accessToken,
}: {
  guid: string;
  videoApiURL: string;
  contentUrl?: string;
  accessToken?: string | null;
}): Promise<{
  metadata: Metadata;
  source?: Source;
  tracks: Track[];
  isPvx: boolean;
} | null> {
  const isPvx = contentUrl?.startsWith('https://cdn.jwplayer.com/');
  if (contentUrl && isPvx) {
    try {
      const endpoint = `https://${window.isProduction ? 'prd' : 'acc'}.pvx.talpa.network/media/${guid}${accessToken ? '?auth=1' : ''}`;
      const response = await fetch(endpoint, {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
      });

      if (!response.ok) {
        throw new Error(`PVX request failed with status ${response.status}`);
      }

      const item: PvxItem = await response.json();

      if (!item?.metadata) {
        noticeError(new Error('Video not found'), getErrorDetails(CUSTOM_ERROR_CODES.PVX_NOT_FOUND));
        return null;
      }
      console.log(item);
      if (item.sources) {
        let source = item.sources.find(({ type }) => type === 'application/dash+xml');
        if (!source) throw new Error('No dash found in sources for PVX Item');
        if (source.drm?.playready) {
          delete source.drm.playready;
        }
        return {
          isPvx: true,
          metadata: item.metadata,
          source,
          tracks: [],
        };
      }

      const { metadata } = item;

      const sourceType = contentUrl.includes('.m3u8') ? 'application/vnd.apple.mpegurl' : 'application/dash+xml';

      let resolvedUrl = contentUrl;
      if (
        sourceType === 'application/vnd.apple.mpegurl' &&
        contentUrl.startsWith('https://cdn.jwplayer.com/live/broadcast')
      ) {
        const manifestResponse = await fetch(contentUrl, { redirect: 'follow' });
        resolvedUrl = manifestResponse.url;
      }

      return {
        isPvx: true,
        metadata,
        tracks: [],
        source: {
          file: resolvedUrl,
          type: sourceType,
        },
      };
    } catch (e) {
      noticeError(new Error('Failed to fetch PVX video'), {
        error_message: (e as Error)?.message,
        ...getErrorDetails(CUSTOM_ERROR_CODES.PVX_UNKNOWN),
      });
      return null;
    }
  }

  try {
    const query = encodeURIComponent(
      `query GetVideo { programs(guid: "${guid}") { items { metadata, media { type availableDate } tracks { label kind file } sources { file type drm } } } }`,
    );

    const response = await fetch(`${videoApiURL}/graphql?query=${query}`, {
      method: 'GET',
      headers: window.isProduction ? {} : { 'x-client-details': 'qa' },
    });
    const { data, errors } = await response.json();

    if (errors?.length) {
      const [error] = errors;
      noticeError(new Error('Failed to fetch program'), {
        graphql_error: error.message,
        video_api_url: videoApiURL,
        graphql_query: query,
        ...getErrorDetails(CUSTOM_ERROR_CODES.PROGRAM_ERROR),
      });
      return null;
    }

    const { programs } = data;

    const [video] = programs.items as { media: Media[]; metadata: Metadata; sources: Source[]; tracks: Track[] }[];
    if (!video) {
      noticeError(new Error('Video not found'), getErrorDetails(CUSTOM_ERROR_CODES.PROGRAM_NOT_FOUND));
      return null;
    }

    const { media, metadata, tracks } = video;
    let { sources } = video;

    const { availableDate } = media[0] ?? {};

    if (sources.length === 0 && availableDate && Date.now() >= +availableDate) {
      sources = await refetchSources(guid, videoApiURL);
      customInteraction(`Sources refetch: ${sources.length} sources found`);
    }

    // Fix for live streams
    if (media[0]?.type === 'LIVE') {
      metadata.c_media_playbacktype = 'live';
      metadata.media_duration = 0;
      metadata.c_media_live = true;
      metadata.media_videotype = 'live';
    }

    let source = sources.find(({ type, drm }) => type === 'dash' && (drm === null || !!drm?.widevine));

    if (!source) {
      source = sources.find(({ type }) => type === 'm3u8');
    }

    if (!source) {
      noticeError(new Error('No valid sources found'), {
        ...getErrorDetails(CUSTOM_ERROR_CODES.PROGRAM_NO_SOURCES),
      });
    }

    return {
      isPvx: false,
      metadata,
      source,
      tracks: tracks.filter((track) => track.kind !== 'thumbnails'),
    };
  } catch (error) {
    console.error(error);
    noticeError(new Error('Failed to fetch program'), {
      graphql_error: (error as any)?.message,
      ...getErrorDetails(CUSTOM_ERROR_CODES.PROGRAM_UNKNOWN),
    });
  }
  return null;
}
